<template>
  <v-container fluid class="mx-0 px-0">
    <v-data-table
      :headers="filteredHeaders"
      :items="itemsList"
      :item-class="rowClass"
      :search="search"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :hide-default-footer="hideFooter"
      :sort-by.sync="pagination.sort"
      :sort-desc.sync="pagination.sortDesc"
      :server-items-length="pagination.total"
      :items-per-page="pagination.pageSize"
      :page.sync="pagination.page"
      @update:options="updatePagination"
      :expanded.sync="expanded"
      single-expand
      item-key="pai_id"
    >
      <template #[`item.pai_busena_pavadinimas`]="{ item }">
        <el-busena
          :busena="item.pai_busena_pavadinimas"
          :color="item.busenaColor"
        />
      </template>
      <template #[`item.pai_duomenu_naudojimo_tikslas`]="{ item }">
        <div class="text-cut-4">
          {{ item.pai_duomenu_naudojimo_tikslas }}
        </div>
      </template>
      <template #[`item.pai_teisinis_pagrindas`]="{ item }">
        <div class="text-cut-4">
          {{ item.pai_teisinis_pagrindas }}
        </div>
      </template>
      <template #[`item.pagrindas_bylos`]="{ item }">
        <el-byla
          v-for="byla in item.pagrindas_bylos"
          :key="byla.byl_id"
          :byla="byla"
          color="primary"
          with-name
        />
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="nowrap">
          <el-button
            v-if="item.canMoketi"
            class="my-0"
            x-small
            :key="`moketi-${item.pai_id}`"
            :disabled="item.loading"
            @click.native="paieskaMoketi(item)"
            >Mokėti</el-button
          >
          <v-icon
            v-if="item.canView"
            dense
            :color="`${item.expandedResult ? 'primary' : ''}`"
            class="table-action mx-2"
            @click="viewExpandedResult(item)"
          >
            mdi-eye-check
          </v-icon>
        </div>
        <div class="nowrap">
          <el-byla
            v-for="byla in item.bylos"
            :key="byla.byl_id"
            :byla="byla"
            color="primary"
            with-name
          />
        </div>
        <el-button
          v-if="item.canXml"
          :key="`xml-${item.pai_id}`"
          class="my-0"
          x-small
          @click.native="paieskaXml(item.pai_id)"
          >XML</el-button
        >
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pt-4">
          <el-textarea
            readonly
            label="Duomenų naudojimo tikslas"
            v-model="item.pai_duomenu_naudojimo_tikslas"
          />
          <el-textarea
            v-if="showTeisinisPagrindas"
            readonly
            label="Teisinis pagrindas"
            v-model="item.pai_teisinis_pagrindas"
          />
          <el-textarea
            v-if="showAtmetimoPriezastis && item.pai_atmetimo_priezastis"
            readonly
            label="Atmetimo priežastis"
            v-model="item.pai_atmetimo_priezastis"
          />
          <template v-if="item.aktualus_irasai == true && !isSavitarna">
            <el-checkbox
              class="mb-3 mt-0 pt-0"
              label="Tik aktualūs įrašai"
              v-model="item.aktualus_irasai"
              readonly
            />
          </template>
          <paieska-result
            v-if="item.showResult"
            :key="`result-${item.pai_id}`"
            minimal
            :paieska-id="item.pai_id"
            @updateList="updateList"
          />
        </td>
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        Duomenų nėra
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PaieskaResult from './PaieskaResult'
import rowClass from '../../mixins/rowClass'
import math from '../../mixins/math'
import userRole from '../../mixins/userRole'

export default {
  name: 'PaieskaManoList',
  props: {
    paieskos: Array,
    pagination: Object,
    loading: Boolean,
  },
  components: { PaieskaResult },
  mixins: [rowClass, math, userRole],
  data() {
    return {
      expanded: [],
      expandedRow: null,
      search: null,
      itemsPerPageOptions: [10, 50, 100],

      lockInterval: null,
      lockTimeout: 5000,
    }
  },
  computed: {
    ...mapGetters([
      'paieskaPavadinimas',
      'appSavitarna',
      'savitarnaKrepselisUrl',
    ]),
    headers() {
      return [
        {
          text: 'Paieškos numeris',
          align: 'start',
          value: 'pai_numeris',
        },
        {
          text: 'Būsena',
          align: 'start',
          value: 'pai_busena_pavadinimas',
        },
        {
          text: 'Pavadinimas',
          align: 'start',
          value: 'pai_tipas_pavadinimas',
        },
        {
          text: 'Paieška atlikta pagal',
          align: 'start',
          value: 'pai_atlikta_pagal',
        },
        {
          text: 'Duomenų naudojimo tikslas',
          align: 'start',
          value: 'pai_duomenu_naudojimo_tikslas',
          hide: true,
        },
        {
          text: 'Teisinis pagrindas',
          align: 'start',
          value: 'pai_teisinis_pagrindas',
          hide: !this.isSavitarna || true,
        },
        {
          text: 'Pagrindžiantys dokumentai',
          align: 'start',
          value: 'pagrindas_bylos',
          hide: !this.isSavitarna,
        },
        {
          text: 'Kaina, Eur',
          align: 'start',
          value: 'formatedKaina',
          hide: !this.showKaina,
        },
        {
          text: 'Suformavimo data',
          align: 'center',
          value: 'pai_sukurimo_data',
        },
        {
          text: 'Paieškos rezultatas',
          align: 'center',
          value: 'actions',
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.paieskos.map(item => {
        item.expandedResult = false
        item.canView = true
        item.showResult = item.pai_tipas !== 'informacija'

        item.pai_tipas_pavadinimas =
          this.paieskaPavadinimas[item.pai_tipas_kodas] ?? ''
        item.busenaColor = item.pai_busena === 'ivykdyta' ? 'green' : 'default'
        item.formatedKaina = this.moneyformat(item.pai_kaina ?? 0)

        item.canXml =
          (item.pai_tipas === 'turtas' ||
            item.pai_tipas === 'juridinis' ||
            item.pai_tipas === 'fizinis') &&
          this.isLocal

        if (this.isSavitarna) {
          item.showResult = false
          if (item.pai_busena === 'laukia_apmokejimo') {
            item.canMoketi = true
          }

          if (
            item.pai_busena === 'ivykdyta' ||
            item.pai_busena === 'neivykdyta' ||
            item.pai_busena === 'atmesta'
          ) {
            item.showResult = true
          }
        }

        if (item.pai_tipas === 'kodas') {
          item.bylos = []
        }
        item.bylos = item.bylos.map(byla => {
          let iconTitle = 'Išrašas'
          if (byla.byl_tipas === 'santrauka') {
            iconTitle = 'Sąrašas'
          }
          if (byla.byl_tipas === 'patikra') {
            iconTitle = 'Patikra'
          }
          if (byla.obj_tipas === 'dokumentas') {
            iconTitle = 'Dokumentas'
          }
          if (item.pai_tipas === 'israsas_ntr') {
            iconTitle = 'NTR išrašas'
          }
          return {
            ...byla,
            byl_url: `byla/${byla.byl_id}/${byla.byl_hash}/content`,
            // byl_pavadinimas: item.pai_tipas_pavadinimas,
            obj_tipas: item.obj_tipas,
            icon_title: iconTitle,
          }
        })
        item.pagrindas_bylos = item.pagrindas_bylos.map(byla => {
          byla.byl_tipas = 'pagrindas'
          byla.icon_title = byla.byl_pavadinimas
          return {
            ...byla,
            byl_url: `byla/${byla.byl_id}/${byla.byl_hash}/content`,
            obj_tipas: item.obj_tipas,
          }
        })

        return item
      })
    },
    hideFooter() {
      return this.pagination.total <= 10
    },
    isVykdoma() {
      return (
        this.paieskos.filter(item => item.pai_busena === 'vykdoma').length > 0
      )
    },
    isLocal() {
      return (
        process.env.VUE_APP_LOCAL === 'local' ||
        process.env.VUE_APP_LOCAL === 'develop' ||
        process.env.VUE_APP_LOCAL === 'preprod'
      )
    },
    isSavitarna() {
      return this.appSavitarna
    },
    showTeisinisPagrindas() {
      return this.isSavitarna
    },
    showAtmetimoPriezastis() {
      return this.isSavitarna
    },
    showKaina() {
      return !(
        this.roleRG ||
        this.role('KAC_darbuotojas') ||
        this.roleSkaitytojas
      )
    },
  },
  beforeDestroy() {
    this.stopLoading()
  },
  created() {
    this.checkLoading()
  },
  watch: {
    loading() {
      this.expanded = []
      this.expandedRow = null
    },
  },
  methods: {
    updatePagination(pagination) {
      this.$emit('updatePagination', pagination)
    },
    checkLoading() {
      if (this.isVykdoma) {
        if (!this.lockInterval) {
          this.lockInterval = setInterval(() => {
            this.updateList()
          }, this.lockTimeout)
        }
      } else {
        this.stopLoading()
      }
    },
    stopLoading() {
      clearInterval(this.lockInterval)
    },
    viewExpandedResult(item) {
      item.expandedResult = !item.expandedResult
      this.clearExpanded(item)
      this.expanded = []
      this.expandedRow = null
      if (item.expandedResult) {
        this.expanded = [item]
        this.expandedRow = item.pai_id
      }
    },
    clearExpanded(item) {
      this.paieskos.forEach(e => {
        if (e.pai_id != item.pai_id) {
          e.expandedResult = false
        }
      })
    },
    getActiveClass(item) {
      return item.pai_id === this.expandedRow ? 'row-active' : ''
    },
    updateList() {
      this.$emit('updateList')
      this.checkLoading()
    },
    paieskaXml(paieskaID) {
      this.$axios
        .get(`paieska/${paieskaID}/xml`)
        .then(res => {
          let fileData = res.data
          var fileURL = window.URL.createObjectURL(new Blob([fileData]))
          var fileLink = document.createElement('a')
          fileLink.href = fileURL
          fileLink.setAttribute('download', this.safeFileName('paieska.xml'))
          document.body.appendChild(fileLink)
          fileLink.click()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
    },
    paieskaMoketi(item) {
      let paieskaID = item.pai_id
      this.$emit('clickMoketi', paieskaID, true)

      this.$axios
        .post(`paieska/${paieskaID}/apmoketi`)
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.gotoKrepselis(res.data.krepselis_id)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
          this.$emit('clickMoketi', paieskaID, false)
        })
    },
    gotoKrepselis(krepselis_id = null) {
      if (krepselis_id) {
        window.location.href = `${process.env.VUE_APP_SAVITARNA_GLOBAL_URL}basketDetails.do?basketId=${krepselis_id}`
        return
      }
      window.location.href = this.savitarnaKrepselisUrl
    },
  },
}
</script>
<style lang="scss" scoped>
.table-action {
  line-height: 24px;
}
.table-actions {
  display: inline-flex;
  align-items: flex-start;
  white-space: normal;
}
</style>
